/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
 **************************************************************************************************
 */
import Vue from 'vue';
import Component from 'vue-class-component';
import {Mixins, Ref} from 'vue-property-decorator';

import {BOARD_ANNOTATION_OPTIONS, BOARD_EVENTS, toolRanges} from '@/values/ed-whiteboard.values';
import {HomeworksService} from '@/homework/services/homeworks.service';
import keys from '@/values/keys';
import {RESIZE_TYPES, WBO_CURSOR_TYPES} from '@/core/values/ed-transparency-meeting.values';
import {MeetingsService} from '@/meetings/services/meetings.service';
import BoardAnnotations from '@/homework/models/board-annotations.model';
import PersistentBoardData
    from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/persistent-board-data';
import EdWboDrawer from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/ed-wbo-drawer';
import EdWboDrawerZoom from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/zoom/ed-wbo-drawer-zoom';
import {_MEETINGS_STORE} from '@/meetings/values/meetings.values';
import {HOMEWORKS_EVENTS} from '@/homework/values/homeworks.values';
import {
    CursorStyle,
    OthersCursorMode
} from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-config.enum';
import PersistentBoardDataUtils
    from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/persistent-board-data-utils';
import {BoardMixin} from '@/mixins/common/board.mixin';


@Component({})
/**
 * @deprecated
 * This component is deprecated and will be removed in the future. Use the new EdBoardV2 component instead.
 */
export class EdBoard extends Mixins(BoardMixin) {
    @Ref()
    private readonly svgDefsElement: SVGDefsElement;
    private currentPageClass: string = 'selected-current-page';
    private currentPageBorder: number = 2;
    private persistentBoardData: PersistentBoardData;
    private isRetry: boolean = false;

    private isLoadingAnnotations = false;
    private disableCursor = false;

    get isCursorActive() {
        return this.$store.state.HomeworksStore.isCursorBoardActive;
    }

    private initVariables(): any {
        this.persistentBoardData = new PersistentBoardData(this.currentBoard, this.userId);        

        this.drawer = new EdWboDrawer({
            isHostUser            : this.isHostUser,
            username              : this.userId,
            userFullName          : this.userFullName,
            scale                 : 1.0,
            drawingEvent          : true,
            isIE                  : /MSIE|Trident/.test(window.navigator.userAgent),
            svg                   : this.svgElement,
            drawingArea           : this.drawingAreaElement,
            board                 : this.boardElement,
            pageContainer         : this.pageContainerElement,
            cursors               : this.cursorsElement,
            size                  : toolRanges().chooseSize.value,
            opacity               : toolRanges().chooseOpacity.value,
            boardParameters       : this.boardParameters,
            annotations           : [],
            showMarker            : !this.isPresentationStudentMode,
            showMyCursor          : !this.isPresentationStudentMode,
            showOtherCursors      : true,
            othersCursorMode      : this.isTransparency ?  OthersCursorMode.HostOnly : OthersCursorMode.All,
            othersCursorStyle     : this.noBackground ? CursorStyle.Full : this.isTransparency ? CursorStyle.BorderedCircle : CursorStyle.SolidCircle,
            alwaysShowCursors     : WBO_CURSOR_TYPES.includes(this.boardPage.sectionType),
            messageHooks          : this.noBackground ? [msg => this.resizeBlankCanvas(msg)] : [],
            extraTools            : this.noBackground ? [tools => new EdWboDrawerZoom(tools)] : [],
            persistentBoard       : this.persistentBoardData,
            currentBoard          : this.currentBoard,
            persistenceFunction   : this.saveDataLocally,
            undoRedoStackFunctions: {
                push: this.addBoardActionToStack,
                peek: this.getLastBoardActionFromStack,
                pop: this.removeLastBoardActionFromStack,
                clean: this.cleanBoardActionStack,
                getAll: function (stackName: string): any[] {
                    const boardActionStack = this.$store.state.HomeworksStore[stackName][this.boardName];
                    return boardActionStack ? boardActionStack : [];
                }
            }
        }, this.setCursorBoardMutation.bind(this));
        this.drawer.beforeDraw(this.doBeforeToolDraw);
        this.drawer.onDraw(this.doAfterToolDraw);
        this.drawer.onSend(this.onSend);
        this.drawer.onStopStroke(this.onStopStroke);
        this.drawer.canSendMessage(this.canSendMessage);
        this.drawer.updateBoardWorkInProgress(this.updateBoardWorkInProgress);
        this.drawer.broadcastMessage(this.broadcastMessage);

        this.createEvents();
        if (!this.boardPage.addingPage) {
            this.$edEventBus.$emit(BOARD_EVENTS.INIT_BOARD_TASK, Number(this.currentBoardPage));
        }
        if (!this.socket) {
            this.drawer.unsetCurrentTool();
        }
    }

    /**
     * Callback called by the drawer right after each draw action.
     * @param data
     * @param tool
     * @private
     */
    private doAfterToolDraw(tool: any, data: any): void {
        if (tool?.name !== 'Cursor') {
            this.$edEventBus.$emit(BOARD_EVENTS.DRAWING_TASK);
            if (tool.name === 'Eraser' && data.id) {
                this.drawer.removePencilPathDataCacheById(data.id);
            }
        }
    }

    /**
     * Callback called by the drawer right after each send action.
     */
    private onSend(tool: any, data: any, isMadeByUser: boolean = true): void {
        const toolName = tool.name;
        switch (toolName) {
            case 'Eraser All':
                this.sendRemoveAllByUser();
                break;
            default:
                this.broadcast(tool, data, isMadeByUser);
                break;
        }
    }

    private async onStopStroke(): Promise<void> {
        await this.persistentBoardData.save();
    }

    private deleteBoardFromEraseAllUndoAction(): void {
        let eraseAllUndoAction: any = this.getLastBoardActionFromStack(BOARD_ANNOTATION_OPTIONS.undo);
        delete eraseAllUndoAction[this.currentBoard];

        const annotatedBoards = Object.keys(eraseAllUndoAction).length;
        if (annotatedBoards === 0) {
            this.removeLastBoardActionFromStack(BOARD_ANNOTATION_OPTIONS.undo);
        }
    }

    private sendRemoveAllByUser(): void {
        const boardData: any = this.persistentBoardData.getAnnotationData();
        if (!boardData || Object.keys(boardData).length === 0) {
            this.deleteBoardFromEraseAllUndoAction();
            return;
        }

        let message = {
            board      : this.currentBoard,
            userName   : this.userId,
            isOwner    : this.isOwner,
            isHost     : this.isHostUser,
            operationId: Date.now()
        };
        if (!this.offlineMode && this.socket && !this.socket.disconnected) {
            this.persistentBoardData.saveOperationId(message.operationId);
            this.socketEmit('removeAllByUser', message);
        } else if (this.canSubmit) {
            const boardData: any = this.persistentBoardData.getAnnotationData();
            const elementsToDelete: string[] = Object.keys(boardData).filter(key => boardData[key]?.userName === this.userId);
            if (elementsToDelete && elementsToDelete.length > 0) {
              this.removeElements(elementsToDelete, this.userId);
              this.persistentBoardData.markUnsaved();
            }
        }
    }

    private broadcast(tool: any, data: any, isMadeByUser: boolean = true) {
        if(tool.name === 'Cursor' && !this.isCursorActive) {
            return;
        }

        const message: any = {
            board: this.currentBoard,
            data
        };
        
        if (!this.offlineMode && this.socket && !this.socket.disconnected) {
            message.operationId = Date.now();
            message.isMadeByUser = isMadeByUser;
            if (tool.name == 'Cursor' && this.isTransparency) {
                message.data.isGuest = Boolean(this.isPresentationStudentMode);
            }
            this.socketEmit('broadcast', message);
        }
        if (tool.name !== 'Cursor' && data?.type !== 'finish') {
            this.saveDataLocally({...data, operationId: message.operationId}, true);
        }
    }


    private async saveDataLocally(data: any, isOutboundMessage: boolean = false) {
        let type: string = data.type;
        let annotationId: string = data.id;
        let parentId: string = data.parent;
        let mine = (username: string) => username ? username == this.userId : this.canSubmit;

        if (!mine(data.userName)) {
            return;
        }
        switch (type) {
            case 'delete':
                if (annotationId) await this.persistentBoardData.deleteAnnotation(annotationId);
                break;
            case 'finish':
            case 'update':
                if (annotationId) this.persistentBoardData.updateAnnotation(annotationId, data);
                break;
            case 'child':
                if (parentId) this.persistentBoardData.addChild(parentId, data);
                break;
            default: //Add data
                if (annotationId) await this.persistentBoardData.setAnnotation(annotationId, data);
        }
        this.persistentBoardData.saveOperationId(data.operationId);
        if (isOutboundMessage && (this.offlineMode || this.socket.disconnected)) {
            this.persistentBoardData.markUnsaved();
        }
    }


    private reconnectBoard(): void {
        this.socketEmit('createBoard', this.currentBoard, this.getDisconnectedBoard);
    }

    private getDisconnectedBoard(): void {
        if(this.drawingAreaElement) {
            this.drawingAreaElement.innerHTML = '';
        }
        this.clearPencilPathDataCache();

        this.socketEmit('getboard', this.currentBoard, this.annotationVersion, this.boardPage.width,
            this.boardPage.height, this.isTransparency);
    }

    private createSVGElement(name: string, attrs?: any): Element {
        let elem = document.createElementNS(this.svgElement.namespaceURI, name);
        if (typeof (attrs) !== 'object') {
            return elem;
        }
        Object.keys(attrs).forEach((key, i) => {
            elem.setAttributeNS(null, key, attrs[key]);
        });

        return elem;
    }


    private socketEmit(eventName: string, ...args) {
        if (this.socket) {
            this.socket.emit(eventName, ...args);
        }
    }

    private createBoardPage(pageData: {isTransparency?: boolean}): void {
        this.boardDimensions.height = this.boardPage.height;
        this.boardDimensions.width = this.boardPage.width;

        //Get the board as soon as the page is loaded
        if (!pageData.isTransparency) {
            this.socketEmit('getboard', this.currentBoard, this.annotationVersion,
                this.boardPage.width, this.boardPage.height, false);
        }
        if (this.boardPage.addedPage) {
            if (!this.socket) {
                this.obtainAnnotations();
            }
            if (!this.boardPage.backgroundImageUrl) {
                this.fixPageForDoc(pageData);
                this.loadedImage = true;
                this.emitLoadedBoard();
                return;
            }
        }
        if (this.noBackground) {
            this.fixPageForDoc(pageData);
        }
        else {
            this.fixPageForDoc(pageData);
            this.loadedImage = true;

        }
        if (this.isHistory){
            this.obtainAnnotations();
        }
    }

    private async removeElements(elementsToDelete: string[], username: string): Promise<void> {
        const actionMadeBySesionUser: boolean = (username && this.userId === username);
        if (actionMadeBySesionUser) {
            this.updateBoardWorkInProgress(true);
        }

        this.drawer.removeClientElements(elementsToDelete, username);
        await this.persistentBoardData.deleteAnnotations(elementsToDelete);

        if (actionMadeBySesionUser) {
            this.updateBoardWorkInProgress(false);
        }
    }

    private async obtainAnnotations(): Promise<void> {
        if (this.isLoadingAnnotations) {
            return;
        }
        this.isLoadingAnnotations = true;
        try {
            let couldNotLoadAnnotations = {value: false};
            await this.getAnnotationsByBoard(couldNotLoadAnnotations);

            if (couldNotLoadAnnotations.value) {
                await this.validateObtainAnnotationsRetry();
                return;
            }

            this.loadedAnnotations = true;
            this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, this.loadedBoardsInDom + 1);
            this.emitLoadedBoard();
        } finally {
            this.isLoadingAnnotations = false;
        }
    }

    private async getAnnotationsByBoard(couldNotLoadAnnotations) {
        let boardAnnotations: BoardAnnotations;
        try {
            let service : any = this.isTask ? HomeworksService : MeetingsService;
            if (this.isHistory){
                boardAnnotations = this.history.annotations;
            }else{
                boardAnnotations = await service.obtainBoardAnnotations(this.currentBoard);
            }
            this.isRetry = false;
        }
        catch (reason) {
            this.$EdModal.createMessageModal({
                title: this.$t('courses.info.infoTitle'),
                body: this.$t('courses.errors.errorLoadingAnnotations'),
                type: keys.EVENT_TYPES.DANGER
            });
            couldNotLoadAnnotations.value = true;
            return;
        }
        if (RESIZE_TYPES.includes(this.boardPage.sectionType)) {
            this.svgElement.width.baseVal.value = boardAnnotations.metadata.width;
            this.svgElement.height.baseVal.value = boardAnnotations.metadata.height;
        }
        this.metadata = boardAnnotations.metadata;
        try {
            await this.clearDrawings();
            await this.handleMessage(boardAnnotations);
        } catch {
            return;
        } finally {
            this.saveInitialAnnotationsLocally(boardAnnotations);
            this.fixPageForImage();
        }
    }

    private async validateObtainAnnotationsRetry(): Promise<void> {
        if (this.isRetry) {
            this.isRetry = false;
            this.loadedAnnotations = true;
            this.emitLoadedBoard();
        } else {
            this.isRetry = true;
            this.isLoadingAnnotations = false;
            await this.obtainAnnotations();
        }
    }

    private async clearDrawings(): Promise<void> {
        this.drawingAreaElement.innerHTML = '';
        this.drawer.clearPathDataCache();
        await this.persistentBoardData.deleteBoard();
    }

    private saveInitialAnnotationsLocally(response: BoardAnnotations) {
        if (!this.canSubmit) {
            return;
        }
        let annotations = {};
        response._children
            .filter(child => child.userName ? child.userName == this.userId : this.canSubmit)
            .forEach(child => {
                if (!child.operationId) {
                    child.operationId = Date.now(); // Give an opId to annotations that came from the server without it.
                }
                annotations[child.id] = child;
            });
        this.persistentBoardData.setData(annotations);
        this.persistentBoardData.save();
    }

    private updateBoardAnnotations() {
        this.clearDrawings();
        this.obtainAnnotations();
    }


    private clearLocalAnnotationsBeforeDestroy(): void {
        if (!this.offlineMode && !PersistentBoardDataUtils.isBoardUnsaved(this.userId, this.boardName)) {
            this.persistentBoardData.deleteBoard();
        }
    }


    private updateBoardWorkInProgress(workInProgress: boolean) {
        this.$edEventBus.$emit(BOARD_EVENTS.BOARD_WORK_IN_PROGRESS, this.currentBoard, workInProgress);
    }

    private broadcastMessage(message: any, onDoneCallback: Function) {
        if (!this.offlineMode && this.socket && !this.socket.disconnected) {
            this.socketEmit('broadcast', message, onDoneCallback);
        } else if (onDoneCallback) {
            onDoneCallback();
        }
    }


    mounted() {
        this.$edEventBus.$on(BOARD_EVENTS.ENABLE_BOARD_TOOLS, this.enableDefaultTool);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_TOOGLE_TOOL}-${this.boardName}`, this.changeTool);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_BOARD_COLOR}-${this.boardName}`, this.changeColor);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_TOOL_SIZE}-${this.boardName}`, this.changeToolSize);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_TOOL_OPACITY}-${this.boardName}`, this.changeToolOpacity);
        this.$edEventBus.$on(BOARD_EVENTS.RECONNECT_BOARD, this.reconnectBoard);
        this.$edEventBus.$on(`${this.currentBoard}${BOARD_EVENTS.REMOVE_ELEMENTS}`, this.removeElements);
        this.$edEventBus.$on(`${BOARD_EVENTS.LOADED_PDF_PAGE}${this.currentBoardPage}`, this.createBoardPage);
        this.$edEventBus.$on(`${BOARD_EVENTS.CREATE_BOARD_PAGE}${this.boardPage.page}`, this.createBoardPage);
        this.$edEventBus.$on(`${this.currentBoard}${BOARD_EVENTS.JOINED}`, this.obtainAnnotations);
        this.$edEventBus.$on(`${BOARD_EVENTS.BOARD_ANNOTATIONS_UPDATED}`, this.updateBoardAnnotations);
        this.$edEventBus.$on(`${this.currentBoard}${BOARD_EVENTS.CREATE_BOARD}`, this.reconnectBoard);
        this.$edEventBus.$on(BOARD_EVENTS.RESIZE_BOARD, this.resizeBoard);
        this.$edEventBus.$on(`${BOARD_EVENTS.RESIZE_BOARD}-${this.boardPage.page}`, this.resizeBoard);
        this.$edEventBus.$on(`${HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB}`, this.resizeBoard);


        this.initVariables();
        if (this.boardPage.addedPage) {
            this.createBoardPage({});
        }

        //-- Don't listen message - it's just for show the annotation history
        if (!this.isHistory){
            this.$edEventBus.$on(this.currentBoard, this.handleMessage);
        }

        this.setCurrentMenuOptions();
    }

    beforeDestroy() {
        this.$edEventBus.$off(BOARD_EVENTS.ENABLE_BOARD_TOOLS, this.enableDefaultTool);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_TOOGLE_TOOL}-${this.boardName}`, this.changeTool);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_BOARD_COLOR}-${this.boardName}`, this.changeColor);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_TOOL_SIZE}-${this.boardName}`, this.changeToolSize);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_TOOL_OPACITY}-${this.boardName}`, this.changeToolOpacity);
        this.$edEventBus.$off(BOARD_EVENTS.RECONNECT_BOARD, this.reconnectBoard);
        this.$edEventBus.$off(`${this.currentBoard}${BOARD_EVENTS.REMOVE_ELEMENTS}`, this.removeElements);
        this.$edEventBus.$off(this.currentBoard, this.handleMessage);
        this.$edEventBus.$off(`${BOARD_EVENTS.LOADED_PDF_PAGE}${this.boardPage.page}`, this.createBoardPage);
        this.$edEventBus.$off(`${BOARD_EVENTS.CREATE_BOARD_PAGE}${this.boardPage.page}`, this.createBoardPage);
        this.$edEventBus.$off(`${this.currentBoard}${BOARD_EVENTS.JOINED}`, this.obtainAnnotations);
        this.$edEventBus.$off(`${BOARD_EVENTS.BOARD_ANNOTATIONS_UPDATED}`, this.updateBoardAnnotations);
        this.$edEventBus.$off(`${this.currentBoard}${BOARD_EVENTS.CREATE_BOARD}`, this.reconnectBoard);
        this.$edEventBus.$off(BOARD_EVENTS.RESIZE_BOARD, this.resizeBoard);
        this.$edEventBus.$off(`${BOARD_EVENTS.RESIZE_BOARD}-${this.boardPage.page}`, this.resizeBoard);
        this.$edEventBus.$off(`${HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB}`, this.resizeBoard);

        this.clearLocalAnnotationsBeforeDestroy();
        this.drawer.dispose();
        this.drawer = null;

        this.removeSvgClickEventListener();
    }

}
export default Vue.component('ed-board', EdBoard);
